"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocale, useTranslations } from "next-intl";
import { ArrowUpRight } from "lucide-react";
import { useGetCategories, useGetSkills } from "@/hooks/main-hooks";
import { technologiesFallback } from "@/data/fake-data";

gsap.registerPlugin(ScrollTrigger);

interface Category {
  _id: string;
  name: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
}

interface Skill {
  _id: string;
  name: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Stack — pick an area on one side, the tools for it appear in a single panel on the other. */
export default function MyStack() {
  const t = useTranslations("brand.about.stack");
  const ta = useTranslations("about.stack");
  const locale = useLocale();
  const rtl = locale === "ar";
  const root = useRef<HTMLElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const { data: categoriesData } = useGetCategories("skill");
  const categories: Category[] = useMemo(() => categoriesData?.categories || [], [categoriesData]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId || categories[0]?._id;
  const activeIndex = Math.max(0, categories.findIndex((c) => c._id === activeId));
  const active = categories[activeIndex];

  const { data: skillsResponse } = useGetSkills({ category: activeId || undefined });
  const skills: Skill[] = skillsResponse?.skills || (activeId ? technologiesFallback[activeId] : null) || [];

  const nameOf = (c?: Category) => (c ? (rtl && c.name_ar ? c.name_ar : c.name) : "");
  const description = (rtl && active?.description_ar) || active?.description || ta("fallbackDesc");

  // section entrance
  useEffect(() => {
    if (!root.current || !categories.length) return;
    const mm = gsap.matchMedia(root);
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".sk-line", {
        yPercent: 110,
        duration: 1.2,
        stagger: 0.08,
        ease: "expo.out",
        scrollTrigger: { trigger: ".sk-head", start: "top 80%", once: true },
      });
      gsap.from(".sk-fade", {
        opacity: 0,
        y: 30,
        duration: 1,
        stagger: 0.08,
        ease: "expo.out",
        scrollTrigger: { trigger: ".sk-body", start: "top 85%", once: true },
      });
    });
    return () => mm.revert();
  }, [categories.length]);

  // re-stage the panel whenever the area or its tools change
  useEffect(() => {
    if (!panel.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".sk-swap", { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.05, ease: "expo.out" });
      gsap.fromTo(
        ".sk-tool",
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.035, ease: "expo.out", delay: 0.08 },
      );
    }, panel);
    return () => ctx.revert();
  }, [activeId, skills.length]);

  if (!categories.length) return null;

  return (
    <section ref={root} className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="sk-head mb-16 grid gap-8 md:mb-24 md:grid-cols-[1fr_minmax(0,420px)] md:items-end">
          <div>
            <p className="label mb-6">{t("label")}</p>
            <h2 className="display text-[clamp(2.6rem,7vw,7rem)] text-bone">
              <span className="block overflow-hidden pb-[0.04em]">
                <span className="sk-line block">{t("title1")}</span>
              </span>
              <span className="block overflow-hidden pb-[0.08em]">
                <span className="sk-line block text-ash">{t("title2")}</span>
              </span>
            </h2>
          </div>
          <p className="text-lg leading-relaxed text-ash">{t("sub")}</p>
        </div>

        <div className="sk-body grid gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
          {/* areas */}
          <div className="sk-fade">
            <p className="label mb-4">{t("areas")}</p>
            <div role="tablist" aria-orientation="vertical" className="border-t border-[var(--hair)]">
              {categories.map((c, i) => {
                const isActive = c._id === activeId;
                return (
                  <button
                    key={c._id}
                    type="button"
                    role="tab"
                    id={`sk-tab-${c._id}`}
                    aria-selected={isActive}
                    aria-controls="sk-panel"
                    onClick={() => setSelectedId(c._id)}
                    onMouseEnter={() => setSelectedId(c._id)}
                    data-cursor="hover"
                    className="group relative grid w-full grid-cols-[2.5rem_1fr_auto] items-center gap-4 border-b border-[var(--hair)] py-5 text-start md:py-7"
                  >
                    <span
                      className={`pointer-events-none absolute inset-x-0 bottom-[-1px] h-px origin-left bg-heat transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] rtl:origin-right ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                    <span className={`tabular font-mono text-[12px] transition-colors ${isActive ? "text-heat" : "text-smoke"}`} dir="ltr">
                      {pad(i + 1)}
                    </span>
                    <span
                      className={`font-display text-[clamp(1.35rem,2.6vw,2.4rem)] font-bold leading-tight tracking-[-0.03em] transition-[color,transform] duration-500 ${
                        isActive
                          ? "translate-x-1 text-bone rtl:-translate-x-1"
                          : "text-smoke group-hover:text-ash"
                      }`}
                    >
                      {nameOf(c)}
                    </span>
                    <ArrowUpRight
                      size={20}
                      className={`transition-all duration-500 ${rtl ? "-scale-x-100" : ""} ${
                        isActive ? "text-heat opacity-100" : "opacity-0"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* tools for the active area */}
          <div className="sk-fade lg:sticky lg:top-28 lg:self-start">
            <div
              ref={panel}
              id="sk-panel"
              role="tabpanel"
              aria-labelledby={active ? `sk-tab-${active._id}` : undefined}
              className="rounded-[26px] border border-[var(--hair)] bg-void-2/55 p-6 shadow-[0_40px_90px_-50px_rgba(0,0,0,0.95)] backdrop-blur-xl sm:p-8 md:p-10"
            >
              <div className="sk-swap flex items-start justify-between gap-6">
                <div className="min-w-0">
                  <p className="label mb-3">{nameOf(active)}</p>
                  <p className="max-w-[36ch] text-[clamp(1.15rem,1.7vw,1.5rem)] leading-snug text-bone">{description}</p>
                </div>
                <span className="display latin tabular shrink-0 text-[clamp(2.6rem,4.5vw,4rem)] text-heat" dir="ltr">
                  {pad(skills.length)}
                </span>
              </div>

              <p className="sk-swap label mb-4 mt-10">{t("inUse")}</p>
              <ul className="flex flex-wrap gap-2.5">
                {skills.length
                  ? skills.map((s) => (
                      <li
                        key={s._id}
                        className="sk-tool rounded-full border border-[var(--hair-strong)] bg-void/40 px-4 py-2 font-mono text-[13px] text-bone transition-colors duration-300 hover:border-heat hover:text-heat"
                        dir="ltr"
                      >
                        {s.name}
                      </li>
                    ))
                  : Array.from({ length: 8 }).map((_, i) => (
                      <li key={i} className="h-9 w-24 animate-pulse rounded-full bg-void-3 motion-reduce:animate-none" aria-hidden />
                    ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
